import {CSSProperties, Children, FormEvent, ReactElement} from "react";
import {compose} from "redux";
import {connect} from "react-redux";
import AppBar from "material-ui/AppBar";
import {Table, TableBody, TableRow, TableRowColumn} from "material-ui/Table";
import Dialog from "material-ui/Dialog";
import FlatButton from "material-ui/FlatButton";
import TextField from "material-ui/TextField";
import {Toolbar, ToolbarGroup} from "material-ui/Toolbar";
import {List, ListItem} from "material-ui/List";
import Toggle from "material-ui/Toggle";
import Subheader from "material-ui/Subheader";
import FontIcon from "material-ui/FontIcon";

import {Ganymede} from "@slices/Ganymede/UserManager";
import {GanymedePermissions} from "@slices/GanymedePermissions/Permissions";
import {Permissions} from "@slices/Permissions/Permissions";

import {RootState} from "src/reducers/index";
import {routeComponent, InjectedProps} from "src/utils/router";
import LayoutWithAppBar from "src/components/LayoutWithAppBar";
import memoize from "src/utils/memoize";

import {
  usersRoute, searchProviderChanged, searchTextChanged, searchFormSubmitted,
  userClicked, userDialogClosed, permissionToggled,
} from "./Users.actions";


const dialogPermissions = [
  {
    type: GanymedePermissions.CreateProject,
    label: 'Create Project',
  },
];


const styles = {
  toolbarInput: {
    margin: '0 24px',
    width: 140,
  },

  tableRow: {
    cursor: 'pointer',
  },

  dialog: {
    width: 360,
  },

  dialogLoading: {
    height: 64 * dialogPermissions.length,  // fill space for ListItems
  },
};


interface UsersTableProps {
  users: Ganymede.User[];
  onRowClick(row: number): void;
}

const UsersTable = ({
  users,
  onRowClick,
}: UsersTableProps) => {
  return <Table selectable={false} onCellClick={onRowClick}>
    <TableBody displayRowCheckbox={false}>
      {users.map(user =>
        <TableRow key={user.identity} hoverable
                  style={styles.tableRow}>
          <TableRowColumn>{user.username}</TableRowColumn>
          <TableRowColumn>{user.fullName}</TableRowColumn>
          <TableRowColumn>{user.email}</TableRowColumn>
          <TableRowColumn>
            <FontIcon className="material-icons">settings</FontIcon>
          </TableRowColumn>
        </TableRow>)}
    </TableBody>
  </Table>;
};


type UsersProps = InjectedProps<{style?: CSSProperties}, {}>;

type UserPermissions = {
  [typeId: string]: boolean;
};

const getUserPermissions = memoize(
  (permissions: Permissions.AbstractPermission[]): UserPermissions => {
    const ret: UserPermissions = {};

    for (const {type} of dialogPermissions) {
      ret[type.ice_staticId()] = permissions
        .some(permission => permission instanceof type);
    }

    return ret;
  },
);

const Users = compose(
  routeComponent<{style?: CSSProperties}, {}>(usersRoute),
  connect(
    (state: RootState, props: UsersProps) => {
      return state.routes.users;
    },
    {
      handleProviderChange(e: any, value: string) {
        return searchProviderChanged(value);
      },
      handleSearchChange(e: any, value: string) {
        return searchTextChanged(value);
      },
      handleSearchSubmit(e: FormEvent<any>) {
        e.preventDefault();
        return searchFormSubmitted();
      },
      handleRowClick(row: number) {
        return userClicked({row});
      },
      handleDialogClose() {
        return userDialogClosed();
      },
      handlePermissionToggle(typeId: string, granted: boolean) {
        return permissionToggled({typeId, granted});
      },
    },
  ),
)(({
  style,
  children,

  searchProvider,
  searchText,
  users,
  isLoading,
  isUnknownProvider,
  selectedUserRow,
  selectedUserPermissions,
  pendingPermissions,

  handleProviderChange,
  handleSearchChange,
  handleSearchSubmit,
  handleRowClick,
  handleDialogClose,
  handlePermissionToggle,
}) => {
  const appBar = <AppBar title="Users"
                         showMenuIconButton={false}/>;

  const dialogTitle = selectedUserRow == null || users == null ? null :
    users[selectedUserRow].fullName;

  let dialogContent: ReactElement<any>;

  if (selectedUserPermissions == null) {
    dialogContent = <div style={styles.dialogLoading}>
      <ListItem disabled primaryText="Loading..."/>
    </div>;
  } else {
    const userPermissions = getUserPermissions(selectedUserPermissions);

    dialogContent = <List>
      {dialogPermissions.map(({type, label}) => {
        const typeId = type.ice_staticId();

        const toggled = pendingPermissions.hasOwnProperty(typeId)
          ? pendingPermissions[typeId]
          : userPermissions[typeId];

        const handleToggle = (e: any, toggled: boolean) => {
          handlePermissionToggle(typeId, toggled);
        };

        const toggle = <Toggle toggled={toggled}
                               onToggle={handleToggle}/>;

        return <ListItem key={typeId}
                         primaryText={label}
                         rightToggle={toggle}/>;
      })}
    </List>;
  }

  const dialog = <Dialog title={dialogTitle}
                         contentStyle={styles.dialog}
                         onRequestClose={handleDialogClose}
                         open={selectedUserRow != null}>
    <Subheader>Permissions</Subheader>
    {dialogContent}
  </Dialog>;

  return <LayoutWithAppBar style={style} appBarElement={appBar}>
    {Children.count(children) > 0 ? children : <div>
        <Toolbar>
          <ToolbarGroup firstChild>
            <form onSubmit={handleSearchSubmit}>
              <TextField style={styles.toolbarInput}
                         hintText="Provider"
                         errorText={isUnknownProvider
                                      ? 'Unknown provider' : null}
                         value={searchProvider}
                         onChange={handleProviderChange}/>
              <TextField style={styles.toolbarInput}
                         hintText="User"
                         value={searchText}
                         onChange={handleSearchChange}/>
              <FlatButton label="Search" type="submit"/>
            </form>
          </ToolbarGroup>
        </Toolbar>

        {dialog}

        {isLoading && 'Loading...'}

        {users == null ? null :
          users.length === 0 ? <div>No users</div> :
            <UsersTable users={users} onRowClick={handleRowClick}/>}
      </div>}
  </LayoutWithAppBar>;
});

export default Users;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/routes/users/Users.tsx